import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaGoogle, FaGithub, FaUsers } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [authError, setAuthError] = useState('');

  const { login } = useAuth();
  const navigate = useNavigate();

  const emailValid = /\S+@\S+\.\S+/.test(formData.email);
  const passwordValid = formData.password.length >= 6;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name] || authError) {
      setErrors(prev => ({ ...prev, [name]: '' }));
      setAuthError('');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!emailValid) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (!passwordValid) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setAuthError('');

    try {
      // This should make an API call to your backend
      const success = await login(formData.email, formData.password, formData.rememberMe);

      if (success) {
        toast.success('Login successful! Redirecting...');
        navigate('/dashboard');
      } else {
        setAuthError('Invalid email or password');
        toast.error('Login failed. Please check your credentials.');
      }
    } catch (error) {
      setAuthError('Login failed. Please try again.');
      toast.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4 py-10">
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <h2 className="text-white text-3xl font-bold text-center mb-2 inline-flex items-center justify-center gap-2">
            <FaUsers className="text-purple-300" />
            Community of Hubs
          </h2>
          <h1 className="text-white text-2xl font-semibold text-center">Welcome Back</h1>
          <p className="text-gray-200 text-sm text-center mb-8">Sign in to your Community of Hubs account</p>
        </div>

        {authError && (
          <div className="mb-4 p-3 rounded-lg bg-red-500/20 border border-red-500/50 text-red-200 text-sm text-center">
            {authError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="text-gray-200 text-sm font-medium block mb-2">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="Enter your email"
            />
            {errors.email && <p className="mt-2 text-sm text-rose-500">{errors.email}</p>}
          </div>

          <div>
            <label htmlFor="password" className="text-gray-200 text-sm font-medium block mb-2">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="w-full px-4 py-2 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="Enter your password"
            />
            {errors.password && <p className="mt-2 text-sm text-rose-500">{errors.password}</p>}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <label className="inline-flex items-center gap-2 text-gray-200 text-sm">
              <input
                type="checkbox"
                id="rememberMe"
                name="rememberMe"
                checked={formData.rememberMe}
                onChange={handleChange}
                className="h-4 w-4 rounded border-white/30 text-indigo-600 focus:ring-indigo-500"
              />
              Remember me
            </label>
            {/* <Link to="/forgot-password" className="text-purple-300 hover:text-purple-200 text-sm">Forgot Password?</Link> */}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-2xl bg-gradient-to-r from-indigo-500 via-purple-600 to-cyan-500 px-5 py-3 text-white font-semibold shadow-lg shadow-indigo-500/20 transition duration-200 hover:scale-[1.02] hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-70"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="mt-6 flex items-center gap-3 text-gray-300 text-sm">
          <span className="h-px flex-1 bg-white/20" />
          <span>Or continue with...</span>
          <span className="h-px flex-1 bg-white/20" />
        </div>

        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button
            type="button"
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl border border-white/20 bg-slate-900/70 px-4 py-3 text-white text-sm font-medium transition duration-200 hover:bg-slate-800"
          >
            <FaGoogle />
            Google
          </button>
          <button
            type="button"
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl border border-white/20 bg-slate-900/70 px-4 py-3 text-white text-sm font-medium transition duration-200 hover:bg-slate-800"
          >
            <FaGithub />
            GitHub
          </button>
        </div>

        {/* <p className="mt-6 text-center text-gray-400 text-xs">
          Don't have an account?{' '}
          <Link to="/register" className="text-purple-300 hover:text-purple-200 font-semibold">
            Sign up
          </Link>
        </p> */}
      </div>
    </div>
  );
};

export default Login;